/**
 * @ngdoc service
 * @name AriaService
 * @module mx.app
 *
 * @description
 * Service provides methods to work with ARIA
 */
import type { I18n } from '../../i18n/mx-internationalization';
import type { IAugmentedJQuery, IDeferred, IPromise, IQService, IWindowService } from 'angular';
import type { ConfigService } from '../services/mx-config-service';

export type AriaMessage = {
	message: string;
	delay: number;
	resultDefer: IDeferred<unknown>;
	polite: boolean;
};

export class AriaService {
	public static readonly $inject = ['mx.internationalization', '$q', '$window', 'mx.shell.Config'];
	public ariaMessageQueue: Array<AriaMessage> = [];
	private readingAriaInProgress = false;

	constructor(
		private readonly i18n: I18n,
		private readonly $q: IQService,
		private readonly $window: IWindowService,
		private readonly config: ConfigService
	) {
		$('<div id="mx-shell-aria-polite-message-box" aria-live="polite" role="status" class="mx-aria-only" style="position: absolute;"></div>').appendTo('body');
	}

	/**
	 * @ngdoc method
	 * @name AriaService#isScreenReaderActivated
	 *
	 * @description
	 * Tries to detect if Screen Reader is activated.
	 * It's based on `prefers-reduced-motion` media query.
	 * It's not super-reliable, but should work in most cases.
	 * It's better to use this method only for some critical places
	 * like dramatic reducing performance in some particular places,
	 * and try to avoid/minimize usage for main logic.
	 *
	 * @reutrns {boolean} - true if Screen Reader is activated
	 */
	isScreenReaderActivated(): boolean {
		// For Reports/Printers mode we don't take into account Screen Reader to increase performance
		const isReportMode = !!$(document.body).find('mx-dashboard-report .mx-dashboard-report:visible').length;
		const isPrintMode = this.$window.matchMedia('print').matches;
		if (isReportMode || isPrintMode) {
			return false;
		}

		// If Screen Reader could be detected then use it
		const isScreenReaderActivatedAutoDetected = this.$window.matchMedia('(prefers-reduced-motion: reduce)').matches; // Detects JAWS(Windows), but doesn't detect VoiceOver(MacOS)
		if (isScreenReaderActivatedAutoDetected) {
			return true;
		}

		// Otherwise we use config value
		return this.config.get('enhancedScreenReaderSupport');
	}

	/**
	 * @ngdoc method
	 * @name AriaService#handleAppSwitchingMessage
	 *
	 * @description
	 * Inform blind user about Switching Application
	 *
	 * @param {object} currentApp - current Application
	 */
	handleAppSwitchingMessage(currentApp: { title: string }): void {
		if (this.getAppName(document.referrer) !== this.getAppName(this.$window.location.href)) {
			this.readMessage((this.i18n.get('shell.aria.application-switch') + currentApp.title).replace(/:/g, ' '), 4000, false);
		}
	}

	private getAppName(url: string): string {
		return ((url || '').match(/\/(app-[^/]+)/) || [])[1];
	}

	/**
	 * @ngdoc method
	 * @name AriaService#readMessage
	 *
	 * @description
	 * Inform blind user some message
	 *
	 * @param {string} message - message to be read
	 * @param {number} delay - time, reserved for reading current message (default is `3000` which is 3 seconds)
	 * @param {boolean} polite - Polite is better but message could be not read because of some other messages or some user actions. (Probably should be value by default but first it's need to check all usecases)
	 *
	 * @returns {promise} - resolves when message was read
	 */
	readMessage(message: string, delay = 3000, polite = false): IPromise<unknown> {
		const resultDefer = this.$q.defer();
		this.ariaMessageQueue.push({ message, delay, resultDefer, polite });
		this.readNextMessage();
		return resultDefer.promise;
	}

	private readNextMessage(): void {
		if (this.ariaMessageQueue.length && !this.readingAriaInProgress) {
			this.readingAriaInProgress = true;
			const msgObj = this.ariaMessageQueue.shift();
			if (!msgObj) {
				return;
			}

			let alertMsgEl: JQuery | undefined;
			if (msgObj.polite) {
				$('#mx-shell-aria-polite-message-box').text(msgObj.message);
			} else {
				alertMsgEl = $(`<div class="mx-aria-only accessible-alert" role="alert" aria-live="assertive"></div>`);
				alertMsgEl.text(msgObj.message);
				alertMsgEl.appendTo('body');
			}

			// For Debugging: console.log(`%cARIA ${msgObj.polite ? 'Polite message' : 'ALERT'}: ` + msgObj.message, 'color: DodgerBlue');

			setTimeout(() => {
				if (msgObj.polite) {
					$('#mx-shell-aria-polite-message-box').text('');
				} else if (alertMsgEl) {
					alertMsgEl.remove();
				}
				this.readingAriaInProgress = false;
				msgObj.resultDefer.resolve();
				if (this.ariaMessageQueue.length) {
					this.readNextMessage();
				}
			}, msgObj.delay);
		}
	}

	/**
	 * @ngdoc method
	 * @name AriaService#createPoliteFocusSettingFn
	 *
	 * @description
	 * This method creates function, which remember focused element when function was created
	 * and will set focus to element by given cssSelector when this returned fucntion will be called.
	 * Focus will be moved just if user didn't change it manually.
	 * It can be useful if we need to read ARIA message and set focus after that
	 * in a case if user hasn't focused another element by himself.
	 *
	 * @param {string} cssSelector - css selector of element to be focused
	 *
	 * @returns {promise} - resolves when message was read
	 */
	createPoliteFocusSettingFn(cssSelector: string) {
		let isActual = true;
		const initialActiveElement = document.activeElement;
		function hendleKeyDown(event: JQueryKeyEventObject) {
			if (document.activeElement?.tagName === 'INPUT' && event.which !== 13) {
				isActual = false;
				$(document.body).off('keydown', hendleKeyDown);
			}
			return undefined;
		}
		function hendleKeyDownOnNavigation(event: JQueryKeyEventObject) {
			if (['Tab', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
				isActual = false;
				$(document.body).off('keydown', hendleKeyDownOnNavigation);
			}
		}
		$(document.body).on('keydown', hendleKeyDown);
		$(document.body).on('keydown', '.mx-shell-nav--container', hendleKeyDownOnNavigation);

		return () => {
			setTimeout(() => {
				const activeEl = document.activeElement;
				if (isActual && (activeEl === initialActiveElement || activeEl === document.body)) {
					$(cssSelector).trigger('focus');
				}
			}, 100);
			return initialActiveElement;
		};
	}

	/**
	 * @ngdoc method
	 * @name AriaService#getPrevHeadingLevel
	 *
	 * @description
	 * This method tries to detect previous Heading Level based on current semantic position in DOM-structure.
	 *
	 * @param {object} element - jQuery element
	 * @param {number} waitingTime - time before starting analysis
	 *
	 * @returns {promise<number|undefined>} - previous Heading Level (1-6)
	 */
	async getPrevHeadingLevel(element: IAugmentedJQuery, waitingTime = 0): Promise<number | undefined> {
		return await new Promise((resolve) =>
			setTimeout(() => {
				const prevHeadingEl = $(element).parents().prevAll().addBack().filter(':header, [role=heading]').last();
				let headingLevel;
				if (prevHeadingEl.length) {
					headingLevel = Number(prevHeadingEl.attr('aria-level') || prevHeadingEl[0].tagName[1]) || undefined;
				}
				resolve(headingLevel);
			}, waitingTime)
		);
	}
}
